import React from "react";
import { styled } from "@linaria/react";
import cn from "classnames";
import { color } from "..";
import t from "../i18n";

const DEFAULT_COLORS = [
  "#D40000",
  "#ED6A0C",
  "#FAAB0C",
  "#FAD20C",
  "#85C700",
  "#2DA641",
  "#11A0AD",
  "#1989FA",
  "#155BD4",
  "#363687",
  "#8A3FD4",
  "#FF0093",
];

interface P {
  colors?: string[];
  defaultColor?: string;
  colorValue?: string;
  onColorClick?: (color: string) => void;
  onColorEnter?: (color: string) => void;
  onColorLeave?: (color: string) => void;
  renderColorItemContent?: (color: string) => JSX.Element;
}

const Wrapper = styled.div``;

const DefaultText = styled.div`
  color: ${color.carbon900};
  font-weight: 600;
  margin-bottom: 4px;
`;

const PaletteText = styled.div`
  color: ${color.carbon900};
  font-weight: 600;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const ColorPalette = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columnCount}, 1fr);
  /* grid-template-rows: 20px 20px; */
  column-gap: 3px;
  row-gap: 3px;
`;

const ColorItem = styled.div<{ color: string }>`
  cursor: pointer;
  padding: 1px;
  width: fit-content;
  height: fit-content;

  &:hover {
    outline: ${color.titanium200} solid 1px;
  }

  &.selected {
    --outline-color: ${color.lightNitrogen500_Main};
    outline: var(--outline-color) solid 1px;
  }
`;

const ColorSquare = styled.div<{ color: string }>`
  background-color: ${props => props.color};
  width: 16px;
  height: 16px;
`;

function defaultRenderColorItemContent(color: string) {
  return <ColorSquare color={color} />;
}

function ColorPicker({
  defaultColor,
  colors = DEFAULT_COLORS,
  colorValue,
  onColorClick,
  onColorEnter,
  onColorLeave,
  renderColorItemContent = defaultRenderColorItemContent,
}: P) {
  return (
    <Wrapper>
      {defaultColor && (
        <>
          <DefaultText>{t("color_picker.default")}</DefaultText>
          <ColorItem
            className={cn({ selected: colorValue === defaultColor })}
            color={defaultColor}
            onClick={() => onColorClick?.(defaultColor)}
            onMouseEnter={() => onColorEnter?.(defaultColor)}
            onMouseLeave={() => onColorLeave?.(defaultColor)}
          >
            {renderColorItemContent(defaultColor)}
          </ColorItem>
        </>
      )}
      <PaletteText>{t("color_picker.palette")}</PaletteText>
      <ColorPalette columnCount={Math.ceil(colors.length / 2)}>
        {colors.map(color => (
          <ColorItem
            key={color}
            className={cn({ selected: colorValue === color })}
            color={color}
            onClick={() => onColorClick?.(color)}
            onMouseEnter={() => onColorEnter?.(color)}
            onMouseLeave={() => onColorLeave?.(color)}
          >
            {renderColorItemContent(color)}
          </ColorItem>
        ))}
      </ColorPalette>
    </Wrapper>
  );
}

export default ColorPicker;
